import React, { useState, useEffect, useRef, useCallback } from 'react'
import { toast } from 'react-toastify'
import { apiSantoLog } from '../../../Services/api'
import FeedbackModal, { FeedbackExtra } from '../_shared/FeedbackModal'
import { printRomaneioByDriver } from '../_shared/printRomaneio'
import { playErrorBeep, playWarningBeep, playSuccessBeep } from '../_shared/sounds'
import { normalizeRastreio } from '../_shared/rastreio'
import { formatBeep } from '../_shared/datetime'
import {
  BoxOrdersModalOverlay, BoxOrdersModalBox, BoxOrdersModalHeader, BoxOrdersModalBody,
  ConfScanInput, ConfFeedback, ConfPanel, ConfVolumesList, ConfVolumeRow, BoxModalBtn,
} from './style'

// Data de hoje no horário LOCAL (sem toISOString, que converteria pra UTC).
function todayLocalISO() {
  const today = new Date()
  const pad = (value) => String(value).padStart(2, '0')
  return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`
}

function cleanRecipientName(rawName) {
  return (rawName || '').replace(/\s*-\s*\d{11}\s*$/, '').trim()
}

const EMPTY_FEEDBACK_MODAL = {
  open: false,
  kind: 'error',
  title: '',
  message: '',
  correctBox: null,
  pendingSiblingsCount: null,
  incompleteOrders: null,
  print: null,
  closeOnDismiss: false,
}

/**
 * EmbarkBoxModal — atalho de embarque dentro do Monta Carga.
 *
 * Mesmo fluxo e mesmas regras da página /santolog/embarcar, só que sem sair da
 * tela: o box já vem escolhido pelo card, então começa direto na lista de
 * volumes pendentes.
 *
 *   GET  /box/:id/embark-list  → volumes que ainda precisam ser embarcados
 *   POST /volume/embark        → embarca um volume (bipagem do rastreio)
 *   POST /box/embark           → embarca todos os restantes de uma vez
 *
 * Regras preservadas da página: volume de outro box, pedido com volumes
 * pendentes de coleta, volume já embarcado e impressão do romaneio ao concluir.
 * Única diferença: como o box está fixado pelo card, o volume de outro box é
 * apenas avisado (sem o botão "ir pro box X" da página).
 */
export default function EmbarkBoxModal({ box, onClose, onEmbarked }) {
  const [boxVolumes, setBoxVolumes] = useState([])
  // Dados completos do box (nome + motorista) vindos do embark-list. O card do
  // Monta Carga só conhece id e nome; o motorista é necessário pro romaneio.
  const [boxDetails, setBoxDetails] = useState(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [scanValue, setScanValue] = useState('')
  const [feedback, setFeedback] = useState({ kind: null, message: '' })
  const [feedbackModal, setFeedbackModal] = useState(EMPTY_FEEDBACK_MODAL)

  const scanInputRef = useRef(null)
  const hasEmbarkedSomethingRef = useRef(false)
  const isOpen = !!box

  const focusScanner = useCallback(() => {
    setTimeout(() => scanInputRef.current && scanInputRef.current.focus(), 60)
  }, [])

  // ── Carrega os volumes pendentes assim que o modal abre ───────────────────
  useEffect(() => {
    if (!isOpen) return undefined
    let cancelled = false

    async function loadPendingVolumes() {
      setLoading(true)
      setBoxVolumes([])
      setBoxDetails(null)
      setFeedback({ kind: null, message: 'Carregando volumes…' })
      try {
        const response = await apiSantoLog.get(`/box/${box.id}/embark-list`)
        if (cancelled) return
        const data = response.data || {}
        const boxFromApi = data.box || {}
        const volumes = Array.isArray(data.volumes) ? data.volumes : []
        const total = data.total || volumes.length
        setBoxDetails({
          id: boxFromApi.id || box.id,
          name: boxFromApi.name || box.name,
          driver_id: boxFromApi.driver?.id || boxFromApi.driver_id || null,
          driver_name: boxFromApi.driver?.name || boxFromApi.driver_name || null,
        })
        setBoxVolumes(volumes)
        setFeedback(
          total === 0
            ? { kind: 'ok', message: `📦 ${box.name} está vazio (sem volumes pra embarcar)` }
            : { kind: null, message: `${total} volume${total !== 1 ? 's' : ''} pra embarcar — bipe o rastreio` }
        )
      } catch (error) {
        if (cancelled) return
        console.error('GET /box/:id/embark-list failed:', error)
        const message = error?.response?.data?.error || error?.response?.data?.message || error.message
        playErrorBeep()
        setFeedback({ kind: 'err', message: 'Erro ao carregar: ' + message })
      } finally {
        if (!cancelled) {
          setLoading(false)
          focusScanner()
        }
      }
    }

    hasEmbarkedSomethingRef.current = false
    loadPendingVolumes()
    return () => { cancelled = true }
  }, [isOpen, box, focusScanner])

  const closeModal = useCallback(() => {
    setBoxVolumes([])
    setBoxDetails(null)
    setScanValue('')
    setFeedback({ kind: null, message: '' })
    setFeedbackModal(EMPTY_FEEDBACK_MODAL)
    // Só recarrega a tela de trás se algum volume mudou de fato
    if (hasEmbarkedSomethingRef.current && onEmbarked) onEmbarked()
    hasEmbarkedSomethingRef.current = false
    onClose()
  }, [onClose, onEmbarked])

  function closeFeedbackModal() {
    const shouldCloseEverything = feedbackModal.closeOnDismiss
    setFeedbackModal(EMPTY_FEEDBACK_MODAL)
    if (shouldCloseEverything) closeModal()
    else focusScanner()
  }

  // Imprime o romaneio do motorista do box recém-embarcado (a partir do modal).
  async function handlePrintRomaneio() {
    const printInfo = feedbackModal.print
    if (!printInfo) return
    try {
      const { ok, error } = await printRomaneioByDriver({
        driverId: printInfo.driver_id,
        date: printInfo.date,
      })
      if (!ok && error === 'popup_blocked') {
        toast.warn('Permita pop-ups neste site para imprimir o romaneio.')
      }
    } catch (error) {
      console.error('print romaneio failed:', error)
      toast.error('Erro ao gerar romaneio: ' + (error?.response?.data?.error || error?.response?.data?.message || error.message))
    }
  }

  const pendingVolumes = boxVolumes.filter((volume) => volume.status !== 'embarcado')
  const embarkedCount = boxVolumes.length - pendingVolumes.length
  const boxDriverName = boxDetails ? boxDetails.driver_name : null

  // Dados do romaneio oferecido quando o box é concluído (individual ou massa)
  function buildRomaneioPrintInfo() {
    return {
      driver_id: boxDetails ? boxDetails.driver_id : null,
      driver_name: boxDriverName,
      date: todayLocalISO(),
    }
  }

  // ── Bipagem de um rastreio ────────────────────────────────────────────────
  async function embarkRastreio(rawCode) {
    const rastreio = normalizeRastreio(rawCode) || ''
    if (!rastreio || !box) return

    setBusy(true)
    setFeedback({ kind: null, message: 'Embarcando…' })
    try {
      const response = await apiSantoLog.post('/volume/embark', {
        box_id: box.id,
        rastreio,
      })
      const data = response.data || {}
      const embarkedVolume = data.volume || {}
      const orderStatus = data.order_status
      hasEmbarkedSomethingRef.current = true

      if (data.already_embarked) {
        playWarningBeep()
        setFeedback({ kind: null, message: `⚠ ${rastreio} já estava embarcado` })
        setFeedbackModal({
          ...EMPTY_FEEDBACK_MODAL,
          open: true,
          kind: 'warning',
          title: 'Volume já embarcado',
          message: `O volume ${rastreio} já estava embarcado. Nenhuma ação tomada.`,
        })
      } else {
        playSuccessBeep()
        setFeedback({
          kind: 'ok',
          message: `✓ ${rastreio} embarcado${orderStatus === 'embarcado' ? ' · pedido completo!' : ''}`,
        })
      }

      // Marca o volume como embarcado na lista local (não remove — mostra ✓)
      const updatedVolumes = boxVolumes.map((volume) => {
        const matches = volume.rastreio === rastreio || volume.id === embarkedVolume.id
        return matches ? { ...volume, status: 'embarcado' } : volume
      })
      setBoxVolumes(updatedVolumes)

      // Box completo → modal de conclusão com opção de imprimir o romaneio
      const remainingCount = updatedVolumes.filter((volume) => volume.status !== 'embarcado').length
      if (remainingCount === 0 && updatedVolumes.length > 0) {
        playSuccessBeep()
        setFeedback({ kind: 'ok', message: '✓ Box completo!' })
        setFeedbackModal({
          ...EMPTY_FEEDBACK_MODAL,
          open: true,
          kind: 'success',
          title: 'Box embarcado',
          message: `Todos os volumes do ${box.name} foram embarcados.`,
          print: buildRomaneioPrintInfo(),
          closeOnDismiss: true,
        })
      }
    } catch (error) {
      console.error('POST /volume/embark failed:', error)
      const data = error?.response?.data || {}
      const status = error?.response?.status

      // 422 com actual_box → volume pertence a outro box
      if (status === 422 && data.actual_box) {
        playWarningBeep()
        setFeedbackModal({
          ...EMPTY_FEEDBACK_MODAL,
          open: true,
          kind: 'warning',
          title: 'Volume não pertence a este box',
          message: data.error || 'Volume está em outro box',
          correctBox: data.actual_box,
        })
        setFeedback({ kind: 'err', message: data.error || 'Volume está em outro box' })
        return
      }

      // 422 com pending_siblings_count → pedido com volumes pendentes de coleta
      if (status === 422 && data.pending_siblings_count != null) {
        playWarningBeep()
        const pendingSiblingsCount = data.pending_siblings_count
        setFeedbackModal({
          ...EMPTY_FEEDBACK_MODAL,
          open: true,
          kind: 'warning',
          title: 'Pedido incompleto — não embarcou',
          message: data.error
            || `O pedido tem ${pendingSiblingsCount} volume${pendingSiblingsCount !== 1 ? 's' : ''} ainda pendente${pendingSiblingsCount !== 1 ? 's' : ''} de coleta.`,
          pendingSiblingsCount,
        })
        setFeedback({ kind: 'err', message: `Pedido com ${pendingSiblingsCount} volume(s) pendente(s)` })
        return
      }

      const message = data.error || data.message || error.message
      if (status === 404) {
        playErrorBeep()
        setFeedbackModal({
          ...EMPTY_FEEDBACK_MODAL, open: true, kind: 'error', title: 'Não encontrado', message,
        })
      } else if (status === 422) {
        playWarningBeep()
        setFeedbackModal({
          ...EMPTY_FEEDBACK_MODAL, open: true, kind: 'warning', title: 'Atenção', message,
        })
      } else {
        playErrorBeep()
        setFeedbackModal({
          ...EMPTY_FEEDBACK_MODAL, open: true, kind: 'error', title: 'Erro', message: 'Erro: ' + message,
        })
      }
      setFeedback({ kind: 'err', message: 'Erro: ' + message })
    } finally {
      setBusy(false)
      setScanValue('')
      focusScanner()
    }
  }

  // ── Embarque em massa ─────────────────────────────────────────────────────
  async function embarkAllRemaining() {
    if (!box || pendingVolumes.length === 0) return
    const confirmed = window.confirm(
      `Embarcar todos os ${pendingVolumes.length} volumes restantes do ${box.name}?`
    )
    if (!confirmed) return

    setBusy(true)
    setFeedback({ kind: null, message: 'Embarcando em massa…' })
    try {
      const response = await apiSantoLog.post('/box/embark', { box_id: box.id })
      const data = response.data || {}
      const embarkedVolumesCount = data.embarked_count || 0
      const affectedOrderIds = data.affected_order_ids || []
      const skipped = data.skipped || {}
      const skippedVolumeIds = Array.isArray(skipped.volume_ids) ? skipped.volume_ids : []
      const incompleteOrders = Array.isArray(skipped.incomplete_orders) ? skipped.incomplete_orders : []
      hasEmbarkedSomethingRef.current = true

      // Marca como embarcado SÓ os volumes que não foram pulados pelo backend
      const skippedVolumeIdSet = new Set(skippedVolumeIds)
      setBoxVolumes((previousVolumes) => previousVolumes.map((volume) => (
        skippedVolumeIdSet.has(volume.id) ? volume : { ...volume, status: 'embarcado' }
      )))

      if (incompleteOrders.length > 0) {
        playWarningBeep()
        setFeedbackModal({
          ...EMPTY_FEEDBACK_MODAL,
          open: true,
          kind: 'warning',
          title: 'Embarque parcial — alguns pedidos foram pulados',
          message: `${embarkedVolumesCount} volume${embarkedVolumesCount !== 1 ? 's' : ''} embarcado${embarkedVolumesCount !== 1 ? 's' : ''}, mas ${incompleteOrders.length} pedido${incompleteOrders.length !== 1 ? 's' : ''} não foi embarcado por ter volumes pendentes de coleta.`,
          incompleteOrders,
        })
        setFeedback({
          kind: 'err',
          message: `⚠ ${embarkedVolumesCount} embarcado(s) · ${incompleteOrders.length} pedido(s) pulado(s)`,
        })
      } else {
        playSuccessBeep()
        setFeedbackModal({
          ...EMPTY_FEEDBACK_MODAL,
          open: true,
          kind: 'success',
          title: 'Embarque em massa concluído',
          message: `${embarkedVolumesCount} volume${embarkedVolumesCount !== 1 ? 's' : ''} embarcado${embarkedVolumesCount !== 1 ? 's' : ''} · ${affectedOrderIds.length} pedido${affectedOrderIds.length !== 1 ? 's' : ''} afetado${affectedOrderIds.length !== 1 ? 's' : ''}`,
          print: buildRomaneioPrintInfo(),
          closeOnDismiss: true,
        })
        setFeedback({
          kind: 'ok',
          message: `✓ ${embarkedVolumesCount} volume${embarkedVolumesCount !== 1 ? 's' : ''} embarcado${embarkedVolumesCount !== 1 ? 's' : ''}`,
        })
      }
    } catch (error) {
      console.error('POST /box/embark failed:', error)
      const message = error?.response?.data?.error || error?.response?.data?.message || error.message
      playErrorBeep()
      setFeedbackModal({
        ...EMPTY_FEEDBACK_MODAL,
        open: true,
        kind: 'error',
        title: 'Erro no embarque em massa',
        message: 'Erro: ' + message,
      })
      setFeedback({ kind: 'err', message: 'Erro: ' + message })
    } finally {
      setBusy(false)
      focusScanner()
    }
  }

  function handleScanKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      const code = scanValue.trim()
      if (code) embarkRastreio(code)
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      if (scanValue) setScanValue('')
      else closeModal()
    }
  }

  if (!isOpen) return null

  return (
    <>
      <BoxOrdersModalOverlay $visible onClick={closeModal}>
        <BoxOrdersModalBox onClick={(event) => event.stopPropagation()}>
          <BoxOrdersModalHeader>
            <div className="info">
              <div className="name">🚚 Embarcar {box.name}</div>
              <div className="meta">
                {boxDriverName ? `🚛 ${boxDriverName} · ` : ''}
                {embarkedCount}/{boxVolumes.length} embarcados
                {pendingVolumes.length > 0
                  ? ` · ${pendingVolumes.length} pendente${pendingVolumes.length !== 1 ? 's' : ''}`
                  : ''}
              </div>
            </div>
            <button type="button" className="close" onClick={closeModal}>✕</button>
          </BoxOrdersModalHeader>

          <BoxOrdersModalBody>
            <ConfScanInput
              ref={scanInputRef}
              value={scanValue}
              $kind={feedback.kind}
              onChange={(event) => setScanValue(event.target.value)}
              onKeyDown={handleScanKeyDown}
              placeholder="Bipe o rastreio do volume"
              disabled={busy || loading}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
            />

            <ConfFeedback $kind={feedback.kind}>
              {feedback.message || (busy ? 'Processando…' : 'Aguardando bipe…')}
            </ConfFeedback>

            {pendingVolumes.length > 0 && (
              <BoxModalBtn
                $primary
                type="button"
                onClick={embarkAllRemaining}
                disabled={busy || loading}
                style={{ width: '100%', marginBottom: 14 }}
              >
                ⚡ Embarcar todos ({pendingVolumes.length})
              </BoxModalBtn>
            )}

            {boxVolumes.length > 0 && (
              <ConfPanel style={{ maxWidth: '100%' }}>
                <h4>Volumes ({embarkedCount}/{boxVolumes.length} embarcados)</h4>
                <ConfVolumesList>
                  {boxVolumes.map((volume) => {
                    const isEmbarked = volume.status === 'embarcado'
                    const order = volume.order || {}
                    return (
                      <ConfVolumeRow key={volume.id || volume.rastreio} $embarked={isEmbarked}>
                        <span className="check">{isEmbarked ? '✓' : '○'}</span>
                        <div className="body">
                          <div className="rastreio">{volume.rastreio || '—'}</div>
                          <div className="meta">
                            NF {order.order_number || '—'}
                            {order.recipient_name && ` · ${cleanRecipientName(order.recipient_name)}`}
                            {order.sequence_order != null && ` · seq ${order.sequence_order}`}
                            {order.driver_name && ` · 🚛 ${order.driver_name}`}
                            {volume.first_scanned_at && ` · 🔵 1º beep ${formatBeep(volume.first_scanned_at)}`}
                          </div>
                        </div>
                        <span className="nf">V{volume.volume || volume.volume_number || '?'}</span>
                      </ConfVolumeRow>
                    )
                  })}
                </ConfVolumesList>
              </ConfPanel>
            )}

            {boxVolumes.length === 0 && !loading && (
              <ConfPanel style={{ maxWidth: '100%', textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 6 }}>📦</div>
                <div style={{ fontSize: 13, color: '#8a95a3' }}>
                  Nenhum volume pendente de embarque neste box.
                </div>
              </ConfPanel>
            )}
          </BoxOrdersModalBody>
        </BoxOrdersModalBox>
      </BoxOrdersModalOverlay>

      <FeedbackModal
        visible={feedbackModal.open}
        kind={feedbackModal.kind}
        title={feedbackModal.title}
        message={feedbackModal.message}
        onClose={closeFeedbackModal}
      >
        {/* Conclusão de box: oferece imprimir o romaneio do motorista */}
        {feedbackModal.print && (
          <FeedbackExtra>
            <div className="lbl">Romaneio do motorista</div>
            <div className="val">🚛 {feedbackModal.print.driver_name || 'Sem motorista'}</div>
            <button
              type="button"
              onClick={handlePrintRomaneio}
              style={{
                marginTop: 12,
                width: '100%',
                padding: '10px 16px',
                background: '#00e5a0',
                border: 'none',
                borderRadius: 8,
                color: '#000',
                fontSize: 13,
                fontWeight: 700,
                fontFamily: "'DM Sans',sans-serif",
                cursor: 'pointer',
              }}
            >
              🖨 Imprimir Romaneio
            </button>
          </FeedbackExtra>
        )}

        {feedbackModal.correctBox && (
          <FeedbackExtra>
            <div className="lbl">Box correto</div>
            <div className="val">📦 {feedbackModal.correctBox.name}</div>
            {feedbackModal.correctBox.barcode && (
              <div className="meta">código {feedbackModal.correctBox.barcode}</div>
            )}
          </FeedbackExtra>
        )}

        {/* Individual: pedido com volumes pendentes (POST /volume/embark 422) */}
        {feedbackModal.pendingSiblingsCount != null && (
          <FeedbackExtra>
            <div className="lbl">Volumes pendentes de coleta</div>
            <div className="val">⚠ {feedbackModal.pendingSiblingsCount}</div>
            <div className="meta">Conclua a separação do pedido antes de tentar embarcar.</div>
          </FeedbackExtra>
        )}

        {/* Massa: lista dos pedidos pulados por estarem incompletos */}
        {Array.isArray(feedbackModal.incompleteOrders) && feedbackModal.incompleteOrders.length > 0 && (
          <FeedbackExtra>
            <div className="lbl">Pedidos não embarcados</div>
            <div style={{
              marginTop: 6,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              maxHeight: 240,
              overflowY: 'auto',
            }}>
              {feedbackModal.incompleteOrders.map((order) => (
                <div
                  key={order.order_id || order.order_number}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 10px',
                    borderRadius: 6,
                    background: 'rgba(245, 158, 11, 0.10)',
                    border: '1px solid rgba(245, 158, 11, 0.4)',
                    fontSize: 12,
                  }}
                >
                  <span style={{ fontFamily: "'Space Mono', monospace", fontWeight: 600 }}>
                    NF {order.order_number || '—'}
                  </span>
                  <span style={{ color: '#f59e0b', fontWeight: 600 }}>
                    ⚠ {order.pending_count} pendente{order.pending_count !== 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </div>
            <div className="meta" style={{ marginTop: 8 }}>
              Conclua a separação desses pedidos antes de embarcá-los.
            </div>
          </FeedbackExtra>
        )}
      </FeedbackModal>
    </>
  )
}
